/**
 * GM Missive Report detail page.
 * Shows report details, missive content, and moderation actions.
 */

import { Link, router } from '@inertiajs/react';
import { useState } from 'react';
import GmLayout from '@/layouts/GmLayout';
import { EditorRenderer } from '@/components/Editor/EditorRenderer';
import { FlashMessage } from '@/components/ui/FlashMessage';

/** Resource amounts */
interface Resources {
    tea: number;
    wood: number;
    mystical: number;
    medicinal: number;
    edible: number;
}

/** User reference */
interface UserRef {
    id: number;
    username: string;
}

/** Missive data */
interface MissiveData {
    id: number;
    subject: string;
    content: string;
    sender: UserRef | null;
    recipient: UserRef | null;
    resources_attached: Resources | null;
    has_resources: boolean;
    resources_collected: boolean;
    sent_at: string | null;
}

/** Report data from the backend */
interface ReportData {
    id: number;
    reason: string;
    status: { value: string; label: string };
    gm_notes: string | null;
    missive: MissiveData;
    reporter: UserRef;
    reviewer: UserRef | null;
    created_at: string;
    reviewed_at: string | null;
}

/** Thread message */
interface ThreadMessage {
    id: number;
    subject: string;
    content: string;
    sender: UserRef | null;
    recipient: UserRef | null;
    sent_at: string | null;
}

interface Props {
    report: ReportData;
    thread: ThreadMessage[];
}

/** Format date in a readable format */
function formatDate(isoString: string | null): string {
    if (!isoString) return '';
    return new Date(isoString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/** Status badge colors */
const STATUS_COLORS: Record<string, string> = {
    pending: 'bg-yellow-600',
    reviewed: 'bg-blue-600',
    resolved: 'bg-green-600',
    dismissed: 'bg-slate-600',
};

/** Resource type configuration */
const RESOURCE_TYPES: { key: keyof Resources; label: string; icon: string }[] = [
    { key: 'tea', label: 'Tea', icon: '🍵' },
    { key: 'wood', label: 'Wood', icon: '🪵' },
    { key: 'mystical', label: 'Mystical', icon: '✨' },
    { key: 'medicinal', label: 'Medicinal', icon: '🌿' },
    { key: 'edible', label: 'Edible', icon: '🍎' },
];

/**
 * GM Missive Report Show page.
 */
export default function Show({ report, thread }: Props) {
    const [status, setStatus] = useState(report.status.value);
    const [gmNotes, setGmNotes] = useState(report.gm_notes || '');
    const [isSubmitting, setIsSubmitting] = useState(false);

    /** Handle status update */
    const handleUpdate = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        router.patch(`/gm/missive-reports/${report.id}`, {
            status,
            gm_notes: gmNotes,
        }, {
            onFinish: () => setIsSubmitting(false),
        });
    };

    const isClosed = ['resolved', 'dismissed'].includes(report.status.value);

    return (
        <GmLayout title={`Report #${report.id}`} activePath="/gm/missive-reports">
            <FlashMessage />

            <div className="p-6">
                {/* Header */}
                <div className="flex items-center gap-4 mb-6">
                    <Link
                        href="/gm/missive-reports"
                        className="text-slate-300 hover:text-white transition-colors"
                    >
                        ← Back to Reports
                    </Link>
                    <h1 className="text-2xl font-bold text-white">
                        Report #{report.id}
                    </h1>
                    <span className={`px-2 py-1 text-sm rounded-full text-white ${
                        STATUS_COLORS[report.status.value] || 'bg-slate-600'
                    }`}>
                        {report.status.label}
                    </span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Reported Missive */}
                        <section className="bg-slate-800 rounded-lg border border-red-600/50 p-4">
                            <h2 className="text-lg font-medium text-red-400 mb-4">
                                🚩 Reported Missive
                            </h2>
                            <MissiveCard missive={report.missive} isReported />
                        </section>

                        {/* Thread Context */}
                        {thread.length > 1 && (
                            <section className="bg-slate-800 rounded-lg border border-slate-700 p-4">
                                <h2 className="text-lg font-medium text-slate-300 mb-4">
                                    📜 Thread Context ({thread.length} messages)
                                </h2>
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                    {thread.map((msg) => (
                                        <MissiveCard
                                            key={msg.id}
                                            missive={msg}
                                            isReported={msg.id === report.missive.id}
                                            compact
                                        />
                                    ))}
                                </div>
                            </section>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Report Details */}
                        <section className="bg-slate-800 rounded-lg border border-slate-700 p-4">
                            <h2 className="text-lg font-medium text-slate-300 mb-4">
                                Report Details
                            </h2>
                            <dl className="space-y-3 text-sm">
                                <div>
                                    <dt className="text-slate-400">Reporter</dt>
                                    <dd className="text-white">{report.reporter.username}</dd>
                                </div>
                                <div>
                                    <dt className="text-slate-400">Reported</dt>
                                    <dd className="text-white">{formatDate(report.created_at)}</dd>
                                </div>
                                {report.reviewer && (
                                    <div>
                                        <dt className="text-slate-400">Reviewed by</dt>
                                        <dd className="text-white">{report.reviewer.username}</dd>
                                    </div>
                                )}
                                {report.reviewed_at && (
                                    <div>
                                        <dt className="text-slate-400">Reviewed at</dt>
                                        <dd className="text-white">{formatDate(report.reviewed_at)}</dd>
                                    </div>
                                )}
                            </dl>

                            {/* Report Reason */}
                            <div className="mt-4 pt-4 border-t border-slate-700">
                                <h3 className="text-sm font-medium text-slate-400 mb-2">Reason</h3>
                                <p className="text-white text-sm bg-slate-700 p-3 rounded">
                                    {report.reason}
                                </p>
                            </div>
                        </section>

                        {/* Moderation Form */}
                        {!isClosed && (
                            <section className="bg-slate-800 rounded-lg border border-slate-700 p-4">
                                <h2 className="text-lg font-medium text-slate-300 mb-4">
                                    Moderation Actions
                                </h2>
                                <form onSubmit={handleUpdate} className="space-y-4">
                                    <div>
                                        <label className="block text-sm text-slate-400 mb-2">
                                            Status
                                        </label>
                                        <select
                                            value={status}
                                            onChange={(e) => setStatus(e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                                        >
                                            <option value="pending">Pending</option>
                                            <option value="reviewed">Reviewed</option>
                                            <option value="resolved">Resolved</option>
                                            <option value="dismissed">Dismissed</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm text-slate-400 mb-2">
                                            GM Notes
                                        </label>
                                        <textarea
                                            value={gmNotes}
                                            onChange={(e) => setGmNotes(e.target.value)}
                                            placeholder="Add notes about this report..."
                                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400"
                                            rows={4}
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-800 rounded-lg font-medium transition-colors"
                                    >
                                        {isSubmitting ? 'Updating...' : 'Update Report'}
                                    </button>
                                </form>
                            </section>
                        )}

                        {/* Closed Report Notes */}
                        {isClosed && report.gm_notes && (
                            <section className="bg-slate-800 rounded-lg border border-slate-700 p-4">
                                <h2 className="text-lg font-medium text-slate-300 mb-4">
                                    GM Notes
                                </h2>
                                <p className="text-white text-sm bg-slate-700 p-3 rounded">
                                    {report.gm_notes}
                                </p>
                            </section>
                        )}
                    </div>
                </div>
            </div>
        </GmLayout>
    );
}

/** Missive card component */
function MissiveCard({
    missive,
    isReported = false,
    compact = false,
}: {
    missive: MissiveData | ThreadMessage;
    isReported?: boolean;
    compact?: boolean;
}) {
    const hasResources = 'has_resources' in missive && missive.has_resources;
    const resources = 'resources_attached' in missive ? missive.resources_attached : null;

    return (
        <div className={`p-3 rounded-lg ${
            isReported
                ? 'bg-red-900/20 border border-red-600/30'
                : 'bg-slate-700/50 border border-slate-600'
        }`}>
            {/* Header */}
            <div className="flex items-start justify-between mb-2">
                <div>
                    {!compact && (
                        <h3 className="font-medium text-white">{missive.subject}</h3>
                    )}
                    <p className="text-sm text-slate-400">
                        {missive.sender?.username || 'Unknown'} → {missive.recipient?.username || 'Unknown'}
                    </p>
                </div>
                <span className="text-xs text-slate-500">{formatDate(missive.sent_at)}</span>
            </div>

            {/* Content */}
            <div className={`prose prose-invert prose-sm max-w-none ${compact ? 'line-clamp-3' : ''}`}>
                <EditorRenderer content={missive.content} />
            </div>

            {/* Resources */}
            {hasResources && resources && (
                <div className="mt-3 pt-3 border-t border-slate-600 flex items-center gap-3">
                    <span className="text-sm text-slate-400">🎁 Resources:</span>
                    {RESOURCE_TYPES.map(({ key, icon }) =>
                        resources[key] > 0 ? (
                            <span key={key} className="text-sm text-white">
                                {icon} {resources[key]}
                            </span>
                        ) : null
                    )}
                </div>
            )}
        </div>
    );
}

